/**
 * tcg-filters.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Reusable tag-filtering module for NenSons TCG Market pages.
 *
 * EXPORTS (via window.TCGFilters):
 *   - All tag Maps / Sets  (arquetipoTags, colorTags, setTags, …)
 *   - Helper utilities     (shouldHideTagFromUI, normalizeTag, …)
 *   - State manager        (FilterState)
 *   - UI builder           (attachGroupFilter, renderTagButtons)
 *   - Visibility helper    (updateGroupedFilterVisibility)
 *   - Visual-cue helper    (updateTagVisualIndicators)
 *   - Performance helpers  (indexProductTags, buildTagCounts)
 *   - Filter engine        (filterProducts)
 *   - Reset helper         (resetAllFilters)
 *   - Auto-detection       (autoDetectAndSelectGame)   ← NEW
 *
 * USAGE ON ANY PAGE:
 *   <script src="//www.nensons.com/cdn/shop/t/16/assets/tcg-filters.js?v=..." type="text/javascript"></script>
 *
 *   Then in your page script:
 *     const { filterState, attachGroupFilter, renderTagButtons } = window.TCGFilters;
 *
 *   RECOMMENDED: call indexProductTags before anything else for best performance:
 *     TCGFilters.indexProductTags(products);
 *
 *   AUTO-DETECTION: after indexing, call once to auto-select the dominant game:
 *     TCGFilters.autoDetectAndSelectGame(products, filterState, onChange);
 *
 *   AUTO-DETECTION WITH SEARCH: pass the live result set so detection re-runs
 *   on each search/filter cycle and keeps game selection in sync:
 *     TCGFilters.autoDetectAndSelectGame(currentResults, filterState, onChange);
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function (global) {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════════
     §1  UTILITY HELPERS
  ═══════════════════════════════════════════════════════════════════════════ */

  /** Remove diacritics for accent-insensitive comparisons */
  const stripDiacritics = (s) =>
    String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\ufe20-\ufe2f]/g, '');

  /** Collapse whitespace, trim, (does NOT lowercase — use normLow for that) */
  function normalizeTag(raw) {
    return String(raw ?? '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Normalise + strip diacritics + lowercase  (used in Set keys) */
  const normLow = (s) => stripDiacritics(normalizeTag(s)).toLowerCase();


  /* ─── Tags that must NEVER appear as filter buttons ──────────────────────── */
  const HIDE_TAGS_SET = new Set([
    'usermrkt',
    'envio', 'envío', 'shipping', 'phantom',
  ]);

  /** MAX1 … MAX99 */
  const RX_TAG_MAXN = /^max\d{1,2}$/i;

  /** Any string containing an e-mail address */
  const RX_EMAIL_PART =
    /(^|[\s:;#|,])[^@\s]+@[^@\s]+\.[A-Za-z]{2,}($|[^\w@.])/i;

  /** Grading-company + numeric-grade combos: "BGS 9", "PSA 10", "CGC 9.5", … */
  const RX_COMPANY_GRADE_TAG =
    /^(psa|bgs|beckett|cgc|sgc|gradelat|grade\s*lat|value\s*up|valueup|gxi)\s*\d+(?:\.\d+)?(?:\s+[\w.-]+)*$/i;

  /** Extra noisy internal tags */
  const RX_HIDE_UI_EXTRA = /^(producto\s*duplicado|tienda\s*principal)\.?$/i;

  /**
   * Returns true if this raw tag should be hidden from every filter UI.
   * @param {string} rawTag
   * @returns {boolean}
   */
  function shouldHideTagFromUI(rawTag) {
    const t   = normalizeTag(rawTag);
    if (!t) return true;
    const low = t.toLowerCase();

    if (HIDE_TAGS_SET.has(low))       return true;
    if (RX_TAG_MAXN.test(low))        return true;
    if (RX_EMAIL_PART.test(t))        return true;
    if (RX_COMPANY_GRADE_TAG.test(t)) return true;
    if (RX_HIDE_UI_EXTRA.test(low))   return true;
    return false;
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     PRODUCT TAG INDEX  (performance optimization)
     Precomputes lowercase tag Sets for each product, so filtering never
     re-normalizes tags on every loop iteration.

     Call this ONCE after loading products, before any filter is applied:
       TCGFilters.indexProductTags(products);
  ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Attaches pre-computed tag caches to every product object.
   * @param {Array} products
   */
  function indexProductTags(products) {
    for (const p of products) {
      const raw  = p.tags || [];
      const norm = raw.map(t => normalizeTag(t));
      const low  = norm.map(t => t.toLowerCase());

      p._tagsNormalized    = norm;
      p._tagsLowerSet      = new Set(low);
      p._filterTagsLowerSet = p._tagsLowerSet;   // alias used by attachGroupFilter
    }
  }

  /**
   * Builds a { [normalizedTag]: count } map from an already-indexed product array.
   * Requires indexProductTags() to have been called first.
   * @param {Array} products
   * @returns {object}
   */
  function buildTagCounts(products) {
    const counts = {};
    for (const p of products) {
      for (const t of (p._tagsNormalized || [])) {
        counts[t] = (counts[t] || 0) + 1;
      }
    }
    return counts;
  }

  /**
   * Filters products by the current selected tags.
   * Requires indexProductTags() to have been called first.
   * @param {Array}  products
   * @param {Set}    selectedTags  — filterState.selectedTags
   * @returns {Array}
   */
  function filterProducts(products, selectedTags) {
    if (!selectedTags || selectedTags.size === 0) return products;
    return products.filter(p => {
      const tagSet = p._filterTagsLowerSet;
      if (!tagSet) return false;
      for (const t of selectedTags) {
        if (!tagSet.has(String(t).toLowerCase())) return false;
      }
      return true;
    });
  }

  /**
   * Clears all filter selections, removes `.selected` from every filter button,
   * then calls onChange so the page can re-render.
   * @param {FilterState} filterState
   * @param {function}    [onChange]
   */
  function resetAllFilters(filterState, onChange) {
    filterState.clearAll();
    document.querySelectorAll('.filter-btn.selected')
      .forEach(btn => btn.classList.remove('selected'));
    if (typeof onChange === 'function') onChange();
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §1b  AUTO-DETECTION  ← NEW
     ─────────────────────────────────────────────────────────────────────────
     Scans a product array and programmatically selects the dominant game
     tag ("One Piece TCG" or "Pokemon TCG") in filterState.tagButtons, then
     calls onChange so all downstream UI (dropdowns, visibility, counts) updates.

     DETECTION LOGIC:
       1. Walk every product in `products` (already-filtered set is fine).
       2. For each product tally a score per game based on which game tags it
          carries.  One Piece and Pokémon each have a canonical list of
          "signature" tags — if ANY matches, that game gets +1.
       3. The game with the higher score wins.  On a tie the previous explicit
          user choice is preserved (no accidental switching).
       4. If neither game is represented (e.g. grading-only results) both
          are deselected.
       5. A flag `filterState._gameAutoSelected` tracks whether the current
          game selection was made automatically so pages can differentiate
          between auto and manual selections if needed.

     INTEGRATION GUIDE:
       // ① Index once on page load
       TCGFilters.indexProductTags(allProducts);

       // ② Auto-detect on first render
       TCGFilters.autoDetectAndSelectGame(allProducts, filterState, applyFilters);

       // ③ Re-run after every search / filter change
       //    Pass the currently visible results (BEFORE game-tag filtering)
       //    so detection reflects what the user is actually browsing.
       function applyFilters() {
         let results = TCGFilters.filterProducts(allProducts, filterState.selectedTags);
         renderProducts(results);
         // re-detect on the unfiltered set so a mid-session search doesn't
         // flip the game unexpectedly:
         TCGFilters.autoDetectAndSelectGame(allProducts, filterState, null, { silent: true });
         TCGFilters.updateGroupedFilterVisibility(filterState);
         TCGFilters.updateTagVisualIndicators(filterState, resetBtn);
       }

     OPTIONS:
       options.silent      {boolean}  — update state but do NOT call onChange
       options.force       {boolean}  — override a manual user selection
       options.minProducts {number}   — minimum products a game must have to win
                                        (default: 1)
  ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Canonical "signature" tag sets per game (lowercased, diacritics-stripped).
   * A product earns a point for a game if ANY of these tags appears on it.
   */
  const GAME_SIGNATURES = {
    onePiece: new Set([
      'one piece tcg',
      'one-piece-tcg',
      // OP set prefixes — if a product has "op01", "op02" … it's One Piece
      ...Array.from({ length: 16 }, (_, i) =>
        `op${String(i + 1).padStart(2, '0')}`),
      // Starter deck prefixes
      ...Array.from({ length: 36 }, (_, i) =>
        `st${String(i + 1).padStart(2, '0')}`),
      // Extra-booster prefixes
      'eb01', 'eb02', 'eb03', 'eb04',
      'prb01', 'prb02',
      // A few very OP-specific type tags
      'don!!', 'don!! normal', 'don!! foil', 'don!! gold',
    ]),
    pokemon: new Set([
      'pokemon tcg',
      'pokémon tcg',
      'pokemon-tcg',
      // Pokémon energy types
      'psychic', 'colorless', 'fire', 'fighting', 'grass',
      'electric', 'metal', 'dragon', 'darkness', 'fairy',
      // Pokémon supertypes
      'trainer', 'energy',
      // Very Pokémon-specific rarities
      'rare holo', 'rare holo ex', 'rare holo gx',
      'rare holo v', 'rare holo vmax', 'rare holo vstar',
      'amazing rare', 'radiant rare', 'shiny rare', 'shiny ultra rare',
      'special illustration rare',
    ]),
  };

  /**
   * The canonical tag string added to filterState.tagButtons for each game.
   * Must match exactly what renderTagButtons uses as PRIORITY_TAGS.
   */
  const GAME_TAG = {
    onePiece: 'One Piece TCG',
    pokemon:  'Pokemon TCG',
  };

  /**
   * Detects the dominant game in `products` and updates filterState accordingly.
   *
   * @param {Array}       products    — product array (indexed or raw)
   * @param {FilterState} filterState — state object from createFilterState()
   * @param {function}    [onChange]  — callback to trigger re-render
   * @param {object}      [options]   — { silent, force, minProducts }
   * @returns {{ detected: string|null, scores: object }}
   */
  function autoDetectAndSelectGame(products, filterState, onChange, options = {}) {
  // 🚫 Auto-selection disabled — never pre-select a game filter.
  return { detected: null, scores: {} };
}

  /**
   * Internal: keeps the priority filter buttons' `.selected` class in sync
   * after a programmatic change to filterState.tagButtons.
   * @private
   */
  function _syncGameButtonVisuals(filterState) {
    document.querySelectorAll('.priority-filter-btn').forEach(btn => {
      // btn.textContent is like "One Piece TCG (42)" — extract the tag part
      const tag = btn.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      btn.classList.toggle('selected', filterState.tagButtons.has(tag));
    });
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §2  TAG DICTIONARIES
     Maps: label → [array of raw tag strings that match it]
     Sets: flat collection of raw tag strings
  ═══════════════════════════════════════════════════════════════════════════ */

  /* ── One Piece: Arquetipos ───────────────────────────────────────────────── */
  const arquetipoTags = new Map([
    ['Accino Family',                  ['Accino Family']],
    ['Alabast',                        ['Alabast']],
    ['Alabasta',                       ['Alabasta']],
    ['Alchemi',                        ['Alchemi']],
    ['Alvida Pirates',                 ['Alvida Pirates']],
    ['Amazon Lily',                    ['Amazon Lily']],
    ['Animal',                         ['Animal']],
    ['Animal Kingdom Pirates',         ['Animal Kingdom Pirates']],
    ['Arlong Pirates',                 ['Arlong Pirates']],
    ['Asuka Island',                   ['Asuka Island']],
    ['Baroque Works',                  ['Baroque Works']],
    ['Barto Club',                     ['Barto Club']],
    ['Baterilla',                      ['Baterilla']],
    ['Beautiful Pirates',              ['Beautiful Pirates']],
    ['Bellamy Pirates',                ['Bellamy Pirates']],
    ['Big Mom Pirates',                ['Big Mom Pirates']],
    ['Biological Weapon',              ['Biological Weapon']],
    ['Black Cat Pirates',              ['Black Cat Pirates']],
    ['Blackbeard Pirates',             ['Blackbeard Pirates']],
    ['Blackbeard Pirates Allies',      ['Blackbeard Pirates Allies']],
    ['Bluejam Pirates',                ['Bluejam Pirates']],
    ['Bonney Pirates',                 ['Bonney Pirates']],
    ['Botanist',                       ['Botanist']],
    ['Bowin Island',                   ['Bowin Island']],
    ['Brownbeard Pirates',             ['Brownbeard Pirates']],
    ['Buggy Pirates',                  ['Buggy Pirates']],
    ["Buggy's Delivery",               ["Buggy's Delivery"]],
    ['Caribou Pirates',                ['Caribou Pirates']],
    ['Celestial Dragons',              ['Celestial Dragons']],
    ['CP0',                            ['CP0']],
    ['CP6',                            ['CP6']],
    ['CP7',                            ['CP7']],
    ['CP9',                            ['CP9']],
    ['Cross Guild',                    ['Cross Guild']],
    ['Crown Island',                   ['Crown Island']],
    ['Donquixote Pirates',             ['Donquixote Pirates']],
    ['Drake Pirates',                  ['Drake Pirates']],
    ['Dressrosa',                      ['Dressrosa']],
    ['Drum Kingdom',                   ['Drum Kingdom']],
    ['East Blue',                      ['East Blue']],
    ['Egghead',                        ['Egghead']],
    ['Eldoraggo Crew',                 ['Eldoraggo Crew']],
    ['Evil Black Drum Kingdom',        ['Evil Black Drum Kingdom']],
    ['Fake Straw Hat Crew',            ['Fake Straw Hat Crew']],
    ['Fallen Monk Pirates',            ['Fallen Monk Pirates']],
    ['FILM',                           ['FILM']],
    ['Firetank Pirates',               ['Firetank Pirates']],
    ['Fish-Man',                       ['Fish-Man']],
    ['Fish-Man Island',                ['Fish-Man Island']],
    ['Five Elders',                    ['Five Elders']],
    ['Flevance',                       ['Flevance']],
    ['Flying Pirates',                 ['Flying Pirates']],
    ['Foolshout Island',               ['Foolshout Island']],
    ['Former Arlong Pirates',          ['Former Arlong Pirates']],
    ['Former Baroque Works',           ['Former Baroque Works']],
    ['Former Big Mom Pirates',         ['Former Big Mom Pirates']],
    ['Former CP9',                     ['Former CP9']],
    ['Former Navy',                    ['Former Navy']],
    ['Former Rocks Pirates',           ['Former Rocks Pirates']],
    ['Former Roger Pirates',           ['Former Roger Pirates']],
    ['Former Rolling Pirates',         ['Former Rolling Pirates']],
    ['Former Rumbar Pirates',          ['Former Rumbar Pirates']],
    ['Former Whitebeard Pirates',      ['Former Whitebeard Pirates']],
    ['Foxy Pirates',                   ['Foxy Pirates']],
    ['Frost Moon Village',             ['Frost Moon Village']],
    ['Galley-La Company',              ['Galley-La Company']],
    ['Gasparde Pirates',               ['Gasparde Pirates']],
    ['GERMA 66',                       ['GERMA 66']],
    ['Giant',                          ['Giant']],
    ['Goa Kingdom',                    ['Goa Kingdom']],
    ['Golden Lion Pirates',            ['Golden Lion Pirates']],
    ['Grantesoro',                     ['Grantesoro']],
    ['Gyro Pirates',                   ['Gyro Pirates']],
    ['Happosui Army',                  ['Happosui Army']],
    ['Hawkins Pirates',                ['Hawkins Pirates']],
    ['Heart Pirates',                  ['Heart Pirates']],
    ['Homies',                         ['Homies']],
    ['Impel Down',                     ['Impel Down']],
    ['Jailer Beast',                   ['Jailer Beast']],
    ['Jaya',                           ['Jaya']],
    ['Journalist',                     ['Journalist']],
    ['Kid Pirates',                    ['Kid Pirates']],
    ['King of the Pirates',            ['King of the Pirates']],
    ['Kingdom of GERMA',               ['Kingdom of GERMA']],
    ['Kingdom of Prodence',            ['Kingdom of Prodence']],
    ['Kouzuki Clan',                   ['Kouzuki Clan']],
    ['Krieg Pirates',                  ['Krieg Pirates']],
    ['Kuja Pirates',                   ['Kuja Pirates']],
    ['Kurozumi Clan',                  ['Kurozumi Clan']],
    ['Land of Wano',                   ['Land of Wano']],
    ['Long Ring Long Land',            ['Long Ring Long Land']],
    ['Lulucia Kingdom',                ['Lulucia Kingdom']],
    ['Lunarian',                       ['Lunarian']],
    ['Mary Geoise',                    ['Mary Geoise']],
    ['Mecha Island',                   ['Mecha Island']],
    ['Merfolk',                        ['Merfolk']],
    ['Minks',                          ['Minks']],
    ['Monkey Mountain Alliance',       ['Monkey Mountain Alliance']],
    ['Mountain Bandits',               ['Mountain Bandits']],
    ['Muggy Kingdom',                  ['Muggy Kingdom']],
    ['Mugiwara Chase',                 ['Mugiwara Chase']],
    ['Music',                          ['Music']],
    ['Navy',                           ['Navy']],
    ['Neo Navy',                       ['Neo Navy']],
    ['Neptunian',                      ['Neptunian']],
    ['New Fish-Man Pirates',           ['New Fish-Man Pirates']],
    ['New Giant Pirates',              ['New Giant Pirates']],
    ['ODYSSEY',                        ['ODYSSEY']],
    ['Ohara',                          ['Ohara']],
    ['Omatsuri Island',                ['Omatsuri Island']],
    ['On-Air Pirates',                 ['On-Air Pirates']],
    ['Peachbeard Pirates',             ['Peachbeard Pirates']],
    ['Plague',                         ['Plague']],
    ['Punk Hazard',                    ['Punk Hazard']],
    ['Red-Haired Pirates',             ['Red-Haired Pirates']],
    ['Revolutionary Army',             ['Revolutionary Army']],
    ['Rocks Pirates',                  ['Rocks Pirates']],
    ['Roger Pirates',                  ['Roger Pirates']],
    ['Rumbar Pirates',                 ['Rumbar Pirates']],
    ['Scientist',                      ['Scientist']],
    ['Seraphim',                       ['Seraphim']],
    ['Shandian Warrior',               ['Shandian Warrior']],
    ['Shipbuilding Town',              ['Shipbuilding Town']],
    ['Sky Island',                     ['Sky Island']],
    ['SMILE',                          ['SMILE']],
    ['Sniper Island',                  ['Sniper Island']],
    ['Spade Pirates',                  ['Spade Pirates']],
    ['Sprite',                         ['Sprite']],
    ['Straw Hat Crew',                 ['Straw Hat Crew']],
    ['Supernovas',                     ['Supernovas']],
    ['SWORD',                          ['SWORD']],
    ['The Akazaya Nine',               ['The Akazaya Nine']],
    ['The Flying Fish Riders',         ['The Flying Fish Riders']],
    ['The Four Emperors',              ['The Four Emperors']],
    ['The Franky Family',              ['The Franky Family']],
    ['The House of Lambs',             ['The House of Lambs']],
    ['The Moon',                       ['The Moon']],
    ['The Pirates Fest',               ['The Pirates Fest']],
    ['The Seven Warlords of the Sea',  ['The Seven Warlords of the Sea']],
    ['The Sun Pirates',                ['The Sun Pirates']],
    ['The Tontattas',                  ['The Tontattas']],
    ['The Vinsmoke Family',            ['The Vinsmoke Family']],
    ['Thriller Bark Pirates',          ['Thriller Bark Pirates']],
    ['Treasure Pirates',               ['Treasure Pirates']],
    ['Trump Pirates',                  ['Trump Pirates']],
    ['Vassals',                        ['Vassals']],
    ['Water Seven',                    ['Water Seven']],
    ["Weevil's Mother",                ["Weevil's Mother"]],
    ['Whitebeard Pirates',             ['Whitebeard Pirates']],
    ['Whitebeard Pirates Allies',      ['Whitebeard Pirates Allies']],
    ['Whole Cake Island',              ['Whole Cake Island']],
    ['Windmill Village',               ['Windmill Village']],
    ['World Government',               ['World Government']],
    ['World Pirates',                  ['World Pirates']],
    ['Yonta Maria Fleet',              ['Yonta Maria Fleet']],
  ]);

  /* ── One Piece: Colores ──────────────────────────────────────────────────── */
  const colorTags = new Map([
    ['🟥 Rojo',       ['Rojo']],
    ['🟩 Verde',      ['Verde']],
    ['🟦 Azul',       ['Azul']],
    ['🟪 Morado',     ['Morado']],
    ['⬛ Negro',      ['Negro']],
    ['🟨 Amarillo',   ['Amarillo']],
    ['🌈 Multicolor', ['Multicolor']],
  ]);

  /* ── One Piece: Sets ─────────────────────────────────────────────────────── */
  const setTags = new Map([
    ['OP01 - Romance Dawn',                         ['OP01', 'Romance Dawn']],
    ['OP02 - Paramount War',                        ['OP02', 'Paramount War']],
    ['OP03 - Pillars Of Strenght',                  ['OP03', 'Pillars Of Strenght']],
    ['OP04 - Kingdoms Of Intrigue',                 ['OP04', 'Kingdoms Of Intrigue']],
    ['OP05 - Awakening Of The New Era',             ['OP05', 'Awakening Of The New Era']],
    ['OP06 - Wings Of The Captain',                 ['OP06', 'Wings Of The Captain']],
    ['OP07 - 500 Years In The Future',              ['OP07', '500 Years In The Future']],
    ['OP08 - Two Legends',                          ['OP08', 'Two Legends']],
    ['OP09 - Emperors In The New World',            ['OP09', 'Emperors In The New World']],
    ['OP10 - Royal Blood',                          ['OP10', 'Royal Blood']],
    ['OP11 - A Fist Of Divine Speed',               ['OP11', 'A Fist Of Divine Speed']],
    ['EB01 - Memorial Collection',                  ['EB01', 'Memorial Collection']],
    ['EB02 - Anime 25th Collection',                ['EB02', 'Anime 25th Collection']],
    ['PRB01 - Premium Booster The Best',            ['PRB01', 'Premium Booster The Best']],
    ['OP12 - Legacy Of The Master',                 ['OP12', 'Legacy Of The Master']],
    ['OP13 - Carrying On His Will',                 ['OP13', 'Carrying On His Will']],
    ['PRB02 - Premium Booster The Best Vol.2',      ['PRB02', 'Premium Booster The Best Vol.2']],
    ['EB03 - Heroines Edition',                     ['EB03', 'Heroines Edition']],
    ['OP14 - The Azure Sea\'s Seven',               ['OP14', "The Azure Sea's Seven"]],
    ['EB04 - The Azure Sea\'s Seven',               ['EB04', "The Azure Sea's Seven"]],
    ['OP14-EB04 - The Azure Sea\'s Seven',          ['OP14-EB04', "The Azure Sea's Seven"]],
    ['OP15 - Adventure On Kami\'s Island',          ['OP15', "Adventure On Kami's Island"]],
    ['OP15-EB04 - Adventure On Kami\'s Island',     ['OP15-EB04', "Adventure On Kami's Island"]],
    ['OP16 - The Time Of Battle',                   ['OP16', 'The Time Of Battle']],
  ]);

  /* ── One Piece: Costo ────────────────────────────────────────────────────── */
  const costoTags = new Set([
    'Cost 1','Cost 2','Cost 3','Cost 4','Cost 5',
    'Cost 6','Cost 7','Cost 8','Cost 9','Cost 10',
  ]);

  /* ── One Piece: Carta Tipo ───────────────────────────────────────────────── */
  const cartaTipoTags = new Map([
    ['Personaje', ['Personaje', 'CHARACTER']],
    ['Evento',    ['Evento',    'EVENT']],
    ['Promo',     ['Promo',     'P']],
    ['Lider',     ['Lider',     'L', 'LEADER', 'Leader']],
    ['Escenario', ['Escenario', 'STAGE']],
    ['DON!!',     ['DON!!',     'D']],
  ]);

  /* ── One Piece: Poder ────────────────────────────────────────────────────── */
  const poderTags = new Set([
    'Power 0','Power 1000','Power 2000','Power 3000','Power 4000',
    'Power 5000','Power 6000','Power 7000','Power 8000','Power 9000',
    'Power 10000','Power 11000','Power 12000','Power 13000',
  ]);

  /* ── One Piece: Rareza ───────────────────────────────────────────────────── */
  const rarezaTags = new Map([
    ['Common',        ['C']],
    ['Uncommon',      ['UC']],
    ['Rare',          ['R']],
    ['Super Rare',    ['SR']],
    ['Secret Rare',   ['SEC']],
    ['Treasure Rare', ['TR']],
    ['Special Rare',  ['SP']],
    ['Manga',         ['M']],
  ]);

  /* ── One Piece: Counter ──────────────────────────────────────────────────── */
  const counterTags = new Set(['Counter 1000', 'Counter 2000']);

  /* ── One Piece: Atributo ─────────────────────────────────────────────────── */
  const atributoTags = new Set(['Slash', 'Special', 'Wisdom', 'Strike', 'Ranged']);

  /* ── One Piece: Collections ──────────────────────────────────────────────── */
  const collectionsTags = new Set([
    'Collections',
    'English Version 1st Anniversary Set',
    'GIFT COLLECTION 2023 [GC-01]',
    'Premium Card Collection -FILM RED Edition-',
    'Premium Card Collection -25th Edition-',
    'Japanese 1st Anniversary Set',
  ]);

  /* ── One Piece: Vidas ────────────────────────────────────────────────────── */
  const vidasTags = new Set([
    'Life 1','Life 2','Life 3','Life 4','Life 5','Life 6','Life 7',
  ]);

  /* ── One Piece: Bloque ───────────────────────────────────────────────────── */
  const bloqueTags = new Set([
    'Block 1','Block 2','Block 3','Block 4','Block 5',
    'Block 6','Block 7','Block 8','Block 9','Block 10',
  ]);

  /* ── One Piece: Starter Decks ────────────────────────────────────────────── */
  const starterDeckTags = new Map([
    ['ST01-Straw Hat Crew',                ['ST01 Straw Hat Crew',                  'ST01']],
    ['ST02-Worst Generation',              ['ST02 Worst Generation',                'ST02']],
    ['ST03-The Seven Warlords Of The Sea', ['ST03 The Seven Warlords Of The Sea',   'ST03']],
    ['ST04-Animal Kingdom Pirates',        ['ST04 Animal Kingdom Pirates',          'ST04']],
    ['ST05-ONE PIECE FILM edition',        ['ST05 ONE PIECE FILM edition',          'ST05']],
    ['ST06-Absolute Justice',              ['ST06 Absolute Justice',                'ST06']],
    ['ST07-Big Mom Pirates',               ['ST07 Big Mom Pirates',                 'ST07']],
    ['ST08-Monkey D. Luffy',               ['ST08 Monkey D. Luffy',                 'ST08']],
    ['ST09-STARTER DECK Yamato',           ['ST09 STARTER DECK Yamato',             'ST09']],
    ['ST10-ULTRA DECK The Three Captains', ['ST10 ULTRA DECK The Three Captains',   'ST10']],
    ['ST11-Uta',                           ['ST11 Uta',                             'ST11']],
    ['ST12-Zoro and Sanji',                ['ST12 Zoro and Sanji',                  'ST12']],
    ['ST13-The Three Brothers',            ['ST13 The Three Brothers',              'ST13']],
    ['ST14-3D2Y',                          ['ST14 3D2Y',                            'ST14']],
    ['ST15-Red Edward.Newgate',            ['ST15 Red Edward.Newgate',              'ST15']],
    ['ST16-Green Uta',                     ['ST16 Green Uta',                       'ST16']],
    ['ST17-Blue Donquixote Doflamingo',    ['ST17 Blue Donquixote Doflamingo',      'ST17']],
    ['ST18-Purple Monkey.D.Luffy',         ['ST18 Purple Monkey.D.Luffy',           'ST18']],
    ['ST19-Black Smoker',                  ['ST19 Black Smoker',                    'ST19']],
    ['ST20-Yellow Charlotte Katakuri',     ['ST20 Yellow Charlotte Katakuri',       'ST20']],
    ['ST21-EX GEAR5',                      ['ST21 EX GEAR5',                        'ST21']],
    ['ST22-Ace & Newgate',                 ['ST22 Ace & Newgate',                   'ST22']],
    ['ST23-RED Shanks',                    ['ST23 RED Shanks',                      'ST23']],
    ['ST24-GREEN Jewelry Bonney',          ['ST24 GREEN Jewelry Bonney',            'ST24']],
    ['ST25-BLUE Buggy',                    ['ST25 BLUE Buggy',                      'ST25']],
    ['ST26-PURPLE/BLACK Monkey.D.Luffy',   ['ST26 PURPLE BLACK Monkey.D.Luffy',     'ST26']],
    ['ST27-BLACK Marshall.D.Teach',        ['ST27 BLACK Marshall.D.Teach',          'ST27']],
    ['ST28-GREEN/YELLOW Yamato',           ['ST28 GREEN YELLOW Yamato',             'ST28']],
    ['ST29-Egghead',                       ['ST29 Egghead',                         'ST29']],
    ['ST30-STARTER DECK EX - Luffy & Ace',            ['ST30 STARTER DECK EX Luffy & Ace',            'ST30']],
    ['ST31-STARTER DECK - RED Monkey.D.Luffy',        ['ST31 STARTER DECK RED Monkey.D.Luffy',        'ST31']],
    ['ST32-STARTER DECK - GREEN Roronoa Zoro',        ['ST32 STARTER DECK GREEN Roronoa Zoro',        'ST32']],
    ['ST33-STARTER DECK - BLUE Kuzan',                ['ST33 STARTER DECK BLUE Kuzan',                'ST33']],
    ['ST34-STARTER DECK - PURPLE Charlotte Katakuri', ['ST34 STARTER DECK PURPLE Charlotte Katakuri', 'ST34']],
    ['ST35-STARTER DECK - RED/BLACK Sabo',            ['ST35 STARTER DECK RED BLACK Sabo',            'ST35']],
    ['ST36-STARTER DECK - YELLOW Eustas "Captain" Kid', ['ST36 STARTER DECK YELLOW Eustas Captain Kid', 'ST36']],
  ]);

  /* ── One Piece: DON!! ────────────────────────────────────────────────────── */
  const donTags = new Map([
    ['DON!! Normal', ['DON!! Normal']],
    ['DON!! Foil',   ['DON!! Foil']],
    ['DON!! Gold',   ['DON!! Gold']],
  ]);

  /* ── Grading: Companies ──────────────────────────────────────────────────── */
  const companyTags = new Map([
    ['PSA',      ['PSA',      'company:psa']],
    ['BGS',      ['BGS',      'company:bgs']],
    ['CGC',      ['CGC',      'company:cgc']],
    ['SGC',      ['SGC',      'company:sgc']],
    ['GradeLat', ['GradeLat', 'company:gradelat']],
    ['ValueUp',  ['ValueUp',  'company:valueup']],
    ['GXI',      ['GXI',      'company:gxi']],
  ]);

  /* ── Grading: Grade Numbers ──────────────────────────────────────────────── */
  const gradeNoTags = new Map([
    ['Grade 1',           ['Grade 1',  'grade 1']],
    ['Grade 2',           ['Grade 2',  'grade 2']],
    ['Grade 3',           ['Grade 3',  'grade 3']],
    ['Grade 4',           ['Grade 4',  'grade 4']],
    ['Grade 5',           ['Grade 5',  'grade 5']],
    ['Grade 6',           ['Grade 6',  'grade 6']],
    ['Grade 7',           ['Grade 7',  'grade 7']],
    ['Grade 8',           ['Grade 8',  'grade 8']],
    ['Grade 9',           ['Grade 9',  'grade 9']],
    ['Grade 10',          ['Grade 10', 'grade 10']],
    ['BGS Black Label',   ['slab--bgs-blacklabel', 'Black Label Pristine']],
    ['BGS Gold (≥9.5)',   ['slab--bgs-gold',        'BGS Gold']],
    ['CGC Pristine 10',   ['slab--cgc-pristine10',  'CGC Pristine 10', 'Pristine 10']],
    ['SGC Pristine 10',   ['slab--sgc-pristine10',  'SGC Pristine 10', 'SGC 10 PRI', 'PRI']],
    ['SGC GEM 10',        ['slab--sgc-gem10',        'SGC 10 GEM']],
    ['GradeLat 10 GEM',   ['slab--gradelat-10-gem',  'GradeLat 10 GEM']],
    ['ValueUp 10',        ['slab--valueup-10',        'ValueUp 10']],
    ['GEM',               ['GEM']],
  ]);

  /* ── Pokémon: Rareza ─────────────────────────────────────────────────────── */
  const pokemonRarezaTags = new Map([
    ['ACE SPEC Rare',               ['ACE SPEC Rare']],
    ['Amazing Rare',                ['Amazing Rare']],
    ['Black White Rare',            ['Black White Rare']],
    ['Classic Collection',          ['Classic Collection']],
    ['Common',                      ['Common']],
    ['Double Rare',                 ['Double Rare']],
    ['Hyper Rare',                  ['Hyper Rare']],
    ['Illustration Rare',           ['Illustration Rare']],
    ['LEGEND',                      ['LEGEND']],
    ['Mega Hyper Rare',             ['Mega Hyper Rare']],
    ['Promo',                       ['Promo']],
    ['Radiant Rare',                ['Radiant Rare']],
    ['Rare',                        ['Rare']],
    ['Rare ACE',                    ['Rare ACE']],
    ['Rare BREAK',                  ['Rare BREAK']],
    ['Rare Holo',                   ['Rare Holo']],
    ['Rare Holo EX',                ['Rare Holo EX']],
    ['Rare Holo GX',                ['Rare Holo GX']],
    ['Rare Holo LV.X',              ['Rare Holo LV.X']],
    ['Rare Holo Star',              ['Rare Holo Star']],
    ['Rare Holo V',                 ['Rare Holo V']],
    ['Rare Holo VMAX',              ['Rare Holo VMAX']],
    ['Rare Holo VSTAR',             ['Rare Holo VSTAR']],
    ['Rare Prime',                  ['Rare Prime']],
    ['Rare Prism Star',             ['Rare Prism Star']],
    ['Rare Rainbow',                ['Rare Rainbow']],
    ['Rare Secret',                 ['Rare Secret']],
    ['Rare Shining',                ['Rare Shining']],
    ['Rare Shiny',                  ['Rare Shiny']],
    ['Rare Shiny GX',               ['Rare Shiny GX']],
    ['Rare Ultra',                  ['Rare Ultra']],
    ['Shiny Rare',                  ['Shiny Rare']],
    ['Shiny Ultra Rare',            ['Shiny Ultra Rare']],
    ['Special Illustration Rare',   ['Special Illustration Rare']],
    ['Trainer Gallery Rare Holo',   ['Trainer Gallery Rare Holo']],
    ['Ultra Rare',                  ['Ultra Rare']],
    ['Uncommon',                    ['Uncommon']],
  ]);

  /* ── Pokémon: Tipo ───────────────────────────────────────────────────────── */
  const pokemonTipoTags = new Map([
    ['Psychic',   ['Psychic']],
    ['Water',     ['Water']],
    ['Colorless', ['Colorless']],
    ['Fire',      ['Fire']],
    ['Fighting',  ['Fighting']],
    ['Grass',     ['Grass']],
    ['Electric',  ['Electric']],
    ['Metal',     ['Metal']],
    ['Dragon',    ['Dragon']],
    ['Darkness',  ['Darkness']],
    ['Fairy',     ['Fairy']],
  ]);

  /* ── Pokémon: Supertype ──────────────────────────────────────────────────── */
  const pokemonSupertypeTags = new Map([
    ['Pokémon', ['Pokémon']],
    ['Trainer', ['Trainer']],
    ['Energy',  ['Energy']],
  ]);

  /* ── Pokémon: Sets (full list) ───────────────────────────────────────────── */
  const pokemonSetTags = new Map([
    ['Base SET',                             ['Base SET']],
    ['Jungle SET',                           ['Jungle SET']],
    ['Wizards Black Star Promos SET',        ['Wizards Black Star Promos SET']],
    ['Fossil SET',                           ['Fossil SET']],
    ['Base Set 2',                           ['Base Set 2']],
    ['Team Rocket SET',                      ['Team Rocket SET']],
    ['Gym Heroes SET',                       ['Gym Heroes SET']],
    ['Gym Challenge SET',                    ['Gym Challenge SET']],
    ['Neo Genesis SET',                      ['Neo Genesis SET']],
    ['Neo Discovery SET',                    ['Neo Discovery SET']],
    ['Southern Islands SET',                 ['Southern Islands SET']],
    ['Neo Revelation SET',                   ['Neo Revelation SET']],
    ['Neo Destiny SET',                      ['Neo Destiny SET']],
    ['Legendary Collection SET',             ['Legendary Collection SET']],
    ['Expedition Base Set SET',              ['Expedition Base Set SET']],
    ['Best of Game SET',                     ['Best of Game SET']],
    ['Aquapolis SET',                        ['Aquapolis SET']],
    ['Skyridge SET',                         ['Skyridge SET']],
    ['Ruby & Sapphire SET',                  ['Ruby & Sapphire SET']],
    ['Sandstorm SET',                        ['Sandstorm SET']],
    ['Dragon SET',                           ['Dragon SET']],
    ['Nintendo Black Star Promos SET',       ['Nintendo Black Star Promos SET']],
    ['Team Magma vs Team Aqua SET',          ['Team Magma vs Team Aqua SET']],
    ['EX Trainer Kit Latias SET',            ['EX Trainer Kit Latias SET']],
    ['EX Trainer Kit Latios SET',            ['EX Trainer Kit Latios SET']],
    ['Hidden Legends SET',                   ['Hidden Legends SET']],
    ['FireRed & LeafGreen SET',              ['FireRed & LeafGreen SET']],
    ['POP Series 1 SET',                     ['POP Series 1 SET']],
    ['Team Rocket Returns SET',              ['Team Rocket Returns SET']],
    ['Deoxys SET',                           ['Deoxys SET']],
    ['Emerald SET',                          ['Emerald SET']],
    ['Unseen Forces SET',                    ['Unseen Forces SET']],
    ['POP Series 2 SET',                     ['POP Series 2 SET']],
    ['Delta Species SET',                    ['Delta Species SET']],
    ['Legend Maker SET',                     ['Legend Maker SET']],
    ['EX Trainer Kit 2 Plusle SET',          ['EX Trainer Kit 2 Plusle SET']],
    ['EX Trainer Kit 2 Minun SET',           ['EX Trainer Kit 2 Minun SET']],
    ['POP Series 3 SET',                     ['POP Series 3 SET']],
    ['Holon Phantoms SET',                   ['Holon Phantoms SET']],
    ['Crystal Guardians SET',                ['Crystal Guardians SET']],
    ['POP Series 4 SET',                     ['POP Series 4 SET']],
    ['Dragon Frontiers SET',                 ['Dragon Frontiers SET']],
    ['POP Series 5 SET',                     ['POP Series 5 SET']],
    ['Power Keepers SET',                    ['Power Keepers SET']],
    ['Diamond & Pearl SET',                  ['Diamond & Pearl SET']],
    ['DP Black Star Promos SET',             ['DP Black Star Promos SET']],
    ['Mysterious Treasures SET',             ['Mysterious Treasures SET']],
    ['POP Series 6 SET',                     ['POP Series 6 SET']],
    ['Secret Wonders SET',                   ['Secret Wonders SET']],
    ['Great Encounters SET',                 ['Great Encounters SET']],
    ['POP Series 7 SET',                     ['POP Series 7 SET']],
    ['Majestic Dawn SET',                    ['Majestic Dawn SET']],
    ['Legends Awakened SET',                 ['Legends Awakened SET']],
    ['POP Series 8 SET',                     ['POP Series 8 SET']],
    ['Stormfront SET',                       ['Stormfront SET']],
    ['Platinum SET',                         ['Platinum SET']],
    ['POP Series 9 SET',                     ['POP Series 9 SET']],
    ['Rising Rivals SET',                    ['Rising Rivals SET']],
    ['Supreme Victors SET',                  ['Supreme Victors SET']],
    ['Arceus SET',                           ['Arceus SET']],
    ['Pokemon Rumble SET',                   ['Pokemon Rumble SET']],
    ['HeartGold & SoulSilver SET',           ['HeartGold & SoulSilver SET']],
    ['HGSS Black Star Promos SET',           ['HGSS Black Star Promos SET']],
    ['HS Unleashed SET',                     ['HS Unleashed SET']],
    ['HS Undaunted SET',                     ['HS Undaunted SET']],
    ['HS Triumphant SET',                    ['HS Triumphant SET']],
    ['Call of Legends SET',                  ['Call of Legends SET']],
    ['BW Black Star Promos SET',             ['BW Black Star Promos SET']],
    ['Black & White SET',                    ['Black & White SET']],
    ["McDonald's Collection 2011 SET",       ["McDonald's Collection 2011 SET"]],
    ['Emerging Powers SET',                  ['Emerging Powers SET']],
    ['Noble Victories SET',                  ['Noble Victories SET']],
    ['Next Destinies SET',                   ['Next Destinies SET']],
    ['Dark Explorers SET',                   ['Dark Explorers SET']],
    ["McDonald's Collection 2012 SET",       ["McDonald's Collection 2012 SET"]],
    ['Dragons Exalted SET',                  ['Dragons Exalted SET']],
    ['Dragon Vault SET',                     ['Dragon Vault SET']],
    ['Boundaries Crossed SET',               ['Boundaries Crossed SET']],
    ['Plasma Storm SET',                     ['Plasma Storm SET']],
    ['Plasma Freeze SET',                    ['Plasma Freeze SET']],
    ['Plasma Blast SET',                     ['Plasma Blast SET']],
    ['XY Black Star Promos SET',             ['XY Black Star Promos SET']],
    ['Legendary Treasures SET',              ['Legendary Treasures SET']],
    ['Kalos Starter Set SET',                ['Kalos Starter Set SET']],
    ['XY SET',                               ['XY SET']],
    ['Flashfire SET',                        ['Flashfire SET']],
    ["McDonald's Collection 2014 SET",       ["McDonald's Collection 2014 SET"]],
    ['Furious Fists SET',                    ['Furious Fists SET']],
    ['Phantom Forces SET',                   ['Phantom Forces SET']],
    ['Primal Clash SET',                     ['Primal Clash SET']],
    ['Double Crisis SET',                    ['Double Crisis SET']],
    ['Roaring Skies SET',                    ['Roaring Skies SET']],
    ['Ancient Origins SET',                  ['Ancient Origins SET']],
    ['BREAKthrough SET',                     ['BREAKthrough SET']],
    ["McDonald's Collection 2015 SET",       ["McDonald's Collection 2015 SET"]],
    ['BREAKpoint SET',                       ['BREAKpoint SET']],
    ['Generations SET',                      ['Generations SET']],
    ['Fates Collide SET',                    ['Fates Collide SET']],
    ['Steam Siege SET',                      ['Steam Siege SET']],
    ["McDonald's Collection 2016 SET",       ["McDonald's Collection 2016 SET"]],
    ['Evolutions SET',                       ['Evolutions SET']],
    ['Sun & Moon SET',                       ['Sun & Moon SET']],
    ['SM Black Star Promos SET',             ['SM Black Star Promos SET']],
    ['Guardians Rising SET',                 ['Guardians Rising SET']],
    ['Burning Shadows SET',                  ['Burning Shadows SET']],
    ['Shining Legends SET',                  ['Shining Legends SET']],
    ['Crimson Invasion SET',                 ['Crimson Invasion SET']],
    ["McDonald's Collection 2017 SET",       ["McDonald's Collection 2017 SET"]],
    ['Ultra Prism SET',                      ['Ultra Prism SET']],
    ['Forbidden Light SET',                  ['Forbidden Light SET']],
    ['Celestial Storm SET',                  ['Celestial Storm SET']],
    ['Dragon Majesty SET',                   ['Dragon Majesty SET']],
    ["McDonald's Collection 2018 SET",       ["McDonald's Collection 2018 SET"]],
    ['Lost Thunder SET',                     ['Lost Thunder SET']],
    ['Team Up SET',                          ['Team Up SET']],
    ['Detective Pikachu SET',                ['Detective Pikachu SET']],
    ['Unbroken Bonds SET',                   ['Unbroken Bonds SET']],
    ['Unified Minds SET',                    ['Unified Minds SET']],
    ['Hidden Fates SET',                     ['Hidden Fates SET']],
    ['Hidden Fates Shiny Vault SET',         ['Hidden Fates Shiny Vault SET']],
    ["McDonald's Collection 2019 SET",       ["McDonald's Collection 2019 SET"]],
    ['Cosmic Eclipse SET',                   ['Cosmic Eclipse SET']],
    ['SWSH Black Star Promos SET',           ['SWSH Black Star Promos SET']],
    ['Sword & Shield SET',                   ['Sword & Shield SET']],
    ['Rebel Clash SET',                      ['Rebel Clash SET']],
    ['Darkness Ablaze SET',                  ['Darkness Ablaze SET']],
    ['Pokemon Futsal Collection SET',        ['Pokemon Futsal Collection SET']],
    ["Champion's Path SET",                  ["Champion's Path SET"]],
    ['Vivid Voltage SET',                    ['Vivid Voltage SET']],
    ['Shining Fates SET',                    ['Shining Fates SET']],
    ['Shining Fates Shiny Vault SET',        ['Shining Fates Shiny Vault SET']],
    ['Battle Styles SET',                    ['Battle Styles SET']],
    ['Chilling Reign SET',                   ['Chilling Reign SET']],
    ['Evolving Skies SET',                   ['Evolving Skies SET']],
    ["McDonald's Collection 2021 SET",       ["McDonald's Collection 2021 SET"]],
    ['Celebrations SET',                     ['Celebrations SET']],
    ['Celebrations: Classic Collection SET', ['Celebrations: Classic Collection SET']],
    ['Fusion Strike SET',                    ['Fusion Strike SET']],
    ['Brilliant Stars SET',                  ['Brilliant Stars SET']],
    ['Brilliant Stars Trainer Gallery SET',  ['Brilliant Stars Trainer Gallery SET']],
    ['Astral Radiance SET',                  ['Astral Radiance SET']],
    ['Astral Radiance Trainer Gallery SET',  ['Astral Radiance Trainer Gallery SET']],
    ['Pokemon GO SET',                       ['Pokemon GO SET']],
    ["McDonald's Collection 2022 SET",       ["McDonald's Collection 2022 SET"]],
    ['Lost Origin SET',                      ['Lost Origin SET']],
    ['Lost Origin Trainer Gallery SET',      ['Lost Origin Trainer Gallery SET']],
    ['Silver Tempest SET',                   ['Silver Tempest SET']],
    ['Silver Tempest Trainer Gallery SET',   ['Silver Tempest Trainer Gallery SET']],
    ['Crown Zenith SET',                     ['Crown Zenith SET']],
    ['Crown Zenith Galarian Gallery SET',    ['Crown Zenith Galarian Gallery SET']],
    ['Scarlet & Violet Black Star Promos SET',['Scarlet & Violet Black Star Promos SET']],
    ['Scarlet & Violet Energies SET',        ['Scarlet & Violet Energies SET']],
    ['Scarlet & Violet SET',                 ['Scarlet & Violet SET']],
    ['Paldea Evolved SET',                   ['Paldea Evolved SET']],
    ['Obsidian Flames SET',                  ['Obsidian Flames SET']],
    ['151 SET',                              ['151 SET']],
    ['Paradox Rift SET',                     ['Paradox Rift SET']],
    ['Paldean Fates SET',                    ['Paldean Fates SET']],
    ['Temporal Forces SET',                  ['Temporal Forces SET']],
    ['Twilight Masquerade SET',              ['Twilight Masquerade SET']],
    ['Shrouded Fable SET',                   ['Shrouded Fable SET']],
    ['Stellar Crown SET',                    ['Stellar Crown SET']],
    ['Surging Sparks SET',                   ['Surging Sparks SET']],
    ['Prismatic Evolutions SET',             ['Prismatic Evolutions SET']],
    ['Journey Together SET',                 ['Journey Together SET']],
    ['Destined Rivals SET',                  ['Destined Rivals SET']],
    ['Black Bolt SET',                       ['Black Bolt SET']],
    ['White Flare SET',                      ['White Flare SET']],
    ['Mega Evolution SET',                   ['Mega Evolution SET']],
  ]);


  /* ═══════════════════════════════════════════════════════════════════════════
     §3  POKEMON WHITELIST  (tags allowed to surface in Pokémon filter buttons)
  ═══════════════════════════════════════════════════════════════════════════ */

  const POKEMON_WHITELIST_TAGS = new Set([
    normalizeTag('Pokemon TCG'),
    normalizeTag('Pokémon TCG'),
    ...Array.from(pokemonRarezaTags.values()).flat().map(normalizeTag),
    ...Array.from(pokemonSetTags.values()).flat().map(normalizeTag),
    ...Array.from(pokemonTipoTags.values()).flat().map(normalizeTag),
    ...Array.from(pokemonSupertypeTags.values()).flat().map(normalizeTag),
  ]);


  /* ═══════════════════════════════════════════════════════════════════════════
     §4  ALL-GROUPED-TAGS SET  (prevents grouped tags from leaking into normal buttons)
  ═══════════════════════════════════════════════════════════════════════════ */

  const ALL_GROUPED_TAGS = new Set([
    ...[...arquetipoTags.values()].flat(),
    ...[...colorTags.values()].flat(),
    ...[...setTags.values()].flat(),
    ...costoTags,
    ...[...cartaTipoTags.values()].flat(),
    ...poderTags,
    ...[...rarezaTags.values()].flat(),
    ...counterTags,
    ...atributoTags,
    ...[...starterDeckTags.values()].flat(),
    ...collectionsTags,
    ...vidasTags,
    ...bloqueTags,
    ...[...donTags.values()].flat(),
    ...[...companyTags.values()].flat(),
    ...[...gradeNoTags.values()].flat(),
    ...Array.from(pokemonSetTags.values()).flat(),
    ...Array.from(pokemonTipoTags.values()).flat(),
    ...Array.from(pokemonSupertypeTags.values()).flat(),
    ...Array.from(pokemonRarezaTags.values()).flat(),
  ].map(normLow));

  /* ═══════════════════════════════════════════════════════════════════════════
     §4b  ALL_KNOWN_BUTTON_TAGS
     ─────────────────────────────────────────────────────────────────────────
     The ONLY tags that may appear as flat pill buttons (outside of grouped
     dropdowns). Anything on a product NOT in this set is ignored by
     renderTagButtons — no internal/noisy tags leak through.

     Add entries here whenever a new top-level filter pill is needed.
  ═══════════════════════════════════════════════════════════════════════════ */

  const ALL_KNOWN_BUTTON_TAGS = new Set([
    /* ── Game selectors (always shown as priority pills) ── */
    'One Piece TCG',
    'Pokemon TCG',

    /* ── Add more top-level pills here as needed ── */
    /* e.g. 'Magic TCG', 'Yu-Gi-Oh TCG' */
  ].map(normalizeTag));   // normalised for consistent matching



  /* ═══════════════════════════════════════════════════════════════════════════
     §5  FILTER STATE
  ═══════════════════════════════════════════════════════════════════════════ */

  function createFilterState() {
    const state = {
      tagButtons:      new Set(),
      arquetipos:      new Set(),
      colors:          new Set(),
      sets:            new Set(),
      costos:          new Set(),
      cartaTipos:      new Set(),
      poderes:         new Set(),
      rarezas:         new Set(),
      counters:        new Set(),
      atributos:       new Set(),
      collections:     new Set(),
      vidas:           new Set(),
      bloques:         new Set(),
      starterDecks:    new Set(),
      dons:            new Set(),
      companies:       new Set(),
      gradeNos:        new Set(),
      pokemonSets:     new Set(),
      pokemonSupers:   new Set(),
      pokemonTipos:    new Set(),
      pokemonRarezas:  new Set(),

      /** Tracks which game was last auto-detected ('onePiece' | 'pokemon' | null) */
      _gameAutoSelected: null,

      selectedTags: new Set(),

      rebuild() {
        const flat = (set) => [...set].flatMap(k => k.split('|'));
        this.selectedTags = new Set([
          ...flat(this.tagButtons),
          ...flat(this.arquetipos),
          ...flat(this.colors),
          ...flat(this.sets),
          ...flat(this.costos),
          ...flat(this.cartaTipos),
          ...flat(this.poderes),
          ...flat(this.rarezas),
          ...flat(this.counters),
          ...flat(this.atributos),
          ...flat(this.collections),
          ...flat(this.vidas),
          ...flat(this.bloques),
          ...flat(this.starterDecks),
          ...flat(this.dons),
          ...flat(this.companies),
          ...flat(this.gradeNos),
          ...flat(this.pokemonSets),
          ...flat(this.pokemonSupers),
          ...flat(this.pokemonTipos),
          ...flat(this.pokemonRarezas),
        ]);
      },

      clearAll() {
        [
          'tagButtons','arquetipos','colors','sets','costos','cartaTipos',
          'poderes','rarezas','counters','atributos','collections','vidas',
          'bloques','starterDecks','dons','companies','gradeNos',
          'pokemonSets','pokemonSupers','pokemonTipos','pokemonRarezas',
        ].forEach(k => this[k].clear());
        this._gameAutoSelected = null;
        this.rebuild();
      },
    };
    return state;
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §6  GROUP-FILTER ATTACHMENT
  ═══════════════════════════════════════════════════════════════════════════ */

  function attachGroupFilter(cfg) {
    const {
      toggleId, dropdownId, searchId, optionsId,
      tagsSource, selectedSet, optionClass,
      allProducts, filterState, onChange,
    } = cfg;

    const toggle   = document.getElementById(toggleId);
    const dropdown = document.getElementById(dropdownId);
    const search   = document.getElementById(searchId);
    const options  = document.getElementById(optionsId);
    if (!toggle || !dropdown || !search || !options) return;

    let isOpen = false;

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      isOpen = !isOpen;
      document.querySelectorAll('.relative .absolute').forEach(d => {
        if (d !== dropdown) d.classList.add('hidden');
      });
      if (isOpen) {
        dropdown.classList.remove('hidden');
        search.focus();
        renderOptions(search.value);
      } else {
        dropdown.classList.add('hidden');
      }
    });

    dropdown.addEventListener('click', e => e.stopPropagation());

    document.addEventListener('click', (e) => {
      if (isOpen && !dropdown.contains(e.target) && !toggle.contains(e.target)) {
        dropdown.classList.add('hidden');
        isOpen = false;
      }
    });

    search.addEventListener('input', () => renderOptions(search.value));

    function renderOptions(filter = '') {
      const searchVal = filter.toLowerCase();
      const isMap     = tagsSource instanceof Map;
      const entries   = isMap
        ? Array.from(tagsSource.entries()).filter(([label]) =>
            label.toLowerCase().includes(searchVal))
        : [...tagsSource]
            .filter(t => t.toLowerCase().includes(searchVal))
            .map(t => [t, t]);

      options.innerHTML = '';
      let shown = 0;

      for (const [label, value] of entries) {
        const candidateTags = Array.isArray(value) ? value : [value];
        const candidateKey  = candidateTags.join('|');

        const combined = new Set(filterState.selectedTags);
        if (!selectedSet.has(candidateKey)) {
          candidateTags.forEach(t => combined.add(t));
        }

        /* Count matching products — uses pre-built cache when available */
        let count = 0;
        for (const p of allProducts()) {
          const pSet = p._filterTagsLowerSet || p._tagsLowerSet || new Set();
          let ok = true;
          for (const t of combined) {
            if (!pSet.has(String(t).toLowerCase())) { ok = false; break; }
          }
          if (ok) count++;
        }

        if (count < 1) continue;

        const isSelected = selectedSet.has(candidateKey);
        const div        = document.createElement('div');
        div.className    = `${optionClass}${isSelected ? ' selected' : ''}`;
        div.innerHTML    =
          `${label} <span style="color:#6b7280;font-size:.9em;">(${count})</span>`;

        div.addEventListener('click', (e) => {
          e.stopPropagation();
          if (selectedSet.has(candidateKey)) selectedSet.delete(candidateKey);
          else selectedSet.add(candidateKey);

          filterState.rebuild();
          renderOptions(search.value);
          if (typeof onChange === 'function') onChange();
        });

        options.appendChild(div);
        shown++;
      }

      const MAX_VISIBLE = 12;
      if (shown > MAX_VISIBLE) {
        const rowH = options.firstElementChild?.getBoundingClientRect().height || 32;
        options.style.maxHeight = `${Math.ceil(rowH * MAX_VISIBLE)}px`;
        options.style.overflowY = 'auto';
      } else {
        options.style.maxHeight = '';
      }

      toggle.classList.toggle('has-matches', shown > 0);
      toggle.classList.toggle('no-matches',  shown === 0);
    }

    toggle.__refreshMatches = () => renderOptions(search.value || '');

    const arr = window.__groupToggles || (window.__groupToggles = []);
    if (!arr.includes(toggle)) arr.push(toggle);
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §6b  PRIORITY BUTTON LOADING STATE
     Shows a "Cargando..." overlay over .priority-tag-filter-container the
     instant a game pill is clicked, and hides it once #search-status
     confirms the reload finished (same signal every page using this module
     already updates after a search/filter cycle).
  ═══════════════════════════════════════════════════════════════════════════ */

  function _ensurePriorityLoadingStyles() {
    if (document.getElementById('priority-loading-css')) return;
    const s = document.createElement('style');
    s.id = 'priority-loading-css';
    s.textContent = `
      .priority-tag-filter-container {
        position: relative;
        min-height: 48px;
      }
      .priority-tag-filter-container .ptc-loading-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: rgba(255,255,255,.85);
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        border-radius: inherit;
        z-index: 5;
        font-family: inherit;
        font-size: .85rem;
        font-weight: 700;
        color: #dc2626;
        letter-spacing: .02em;
        opacity: 0;
        pointer-events: none;
        transition: opacity .15s ease;
      }
      .priority-tag-filter-container .ptc-loading-overlay.is-visible {
        opacity: 1;
        pointer-events: auto;
      }
      .priority-tag-filter-container .ptc-loading-overlay .ptc-spinner {
        width: 16px;
        height: 16px;
        border: 2.5px solid rgba(220,38,38,.25);
        border-top-color: #dc2626;
        border-radius: 50%;
        animation: ptcSpin .6s linear infinite;
        flex-shrink: 0;
      }
      @keyframes ptcSpin { to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(s);
  }

  function _showPriorityLoading() {
    _ensurePriorityLoadingStyles();
    const container = document.querySelector('.priority-tag-filter-container');
    if (!container) return;

    let overlay = container.querySelector('.ptc-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'ptc-loading-overlay';
      overlay.innerHTML = '<span class="ptc-spinner"></span><span>Cargando...</span>';
      container.appendChild(overlay);
    }
    overlay.classList.add('is-visible');

    clearTimeout(container._ptcSafetyTimer);
    container._ptcSafetyTimer = setTimeout(_hidePriorityLoading, 6000);

    if (!container._ptcStatusObserverBound) {
      const statusEl = document.getElementById('search-status');
      if (statusEl) {
        const obs = new MutationObserver(() => {
          const txt = (statusEl.textContent || '').trim();
          if (
            txt.includes('Mostrando') ||
            txt.includes('No se encontraron') ||
            txt.includes('Error') ||
            txt.includes('💡') ||
            txt.includes('ℹ️') ||
            txt.includes('⚠️')
          ) {
            _hidePriorityLoading();
          }
        });
        obs.observe(statusEl, { childList: true, subtree: true, characterData: true });
        container._ptcStatusObserverBound = true;
      }
    }
  }

  function _hidePriorityLoading() {
    const container = document.querySelector('.priority-tag-filter-container');
    if (!container) return;
    clearTimeout(container._ptcSafetyTimer);
    const overlay = container.querySelector('.ptc-loading-overlay');
    if (overlay) overlay.classList.remove('is-visible');
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     §7  TAG BUTTON RENDERER
  ═══════════════════════════════════════════════════════════════════════════ */

  function renderTagButtons(cfg) {
  const {
    allProducts = [],
    filterState, onChange,
    priorityContainerSelector = '.priority-tag-filter-container',
    normalContainerSelector   = '.tag-filter-container',
  } = cfg;

  const PRIORITY_TAGS = ['One Piece TCG', 'Pokemon TCG'];
  const NORMAL_BUTTON_TAGS = [...ALL_KNOWN_BUTTON_TAGS]
    .filter(tag => !PRIORITY_TAGS.includes(tag))
    .sort();

  const priorityEl = document.querySelector(priorityContainerSelector);
  const normalEl   = document.querySelector(normalContainerSelector);
  if (!priorityEl || !normalEl) return;

  priorityEl.innerHTML = '';
  normalEl.innerHTML   = '';

  priorityEl.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'gap: 4px',
    'margin: 0 !important',
    'padding: 0 !important',
  ].join(';');

  const existingFrame = priorityEl.closest('.game-selector-frame');
  let container;

  if (existingFrame) {
    container = priorityEl;
    const oldPulse = priorityEl.querySelector('.pulse-dot');
    const oldLabel = priorityEl.querySelector('.game-selector-label-text');
    const oldBtns  = priorityEl.querySelector('div[style*="inline-flex"]');
    if (oldPulse) oldPulse.remove();
    if (oldLabel) oldLabel.remove();
    if (oldBtns)  oldBtns.remove();
  } else {
    const frame = document.createElement('div');
    frame.className = 'game-selector-frame';
    priorityEl.appendChild(frame);
    container = frame;
  }

  if (!document.getElementById('game-frame-css')) {
    const s = document.createElement('style');
    s.id = 'game-frame-css';
    s.textContent = `
      @keyframes frameFadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes selectorPulse {
        0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); transform: scale(1); }
        50%  { box-shadow: 0 0 0 6px rgba(239,68,68,0); transform: scale(1.2); }
        100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); transform: scale(1); }
      }
      @keyframes frameAccent {
        0%   { opacity: 0.4; left: 20%; right: 20%; }
        50%  { opacity: 1;   left: 10%; right: 10%; }
        100% { opacity: 0.4; left: 20%; right: 20%; }
      }

      .game-selector-frame {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        padding: 6px 14px;
        margin: 0;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        flex-wrap: nowrap;
        overflow: visible;
        border: 1.5px solid rgba(239,68,68,0.35);
        border-radius: 16px;
        background: linear-gradient(135deg, rgba(239,68,68,0.04) 0%, rgba(127,29,29,0.06) 100%);
        box-shadow: 0 0 0 1px rgba(239,68,68,0.08), 0 2px 12px rgba(239,68,68,0.10), inset 0 1px 0 rgba(255,255,255,0.6);
        position: relative;
        animation: frameFadeIn 0.4s ease both;
        line-height: 1 !important;
      }
      .game-selector-frame::before {
        content: '';
        position: absolute;
        top: -1.5px;
        left: 20%; right: 20%;
        height: 2px;
        background: linear-gradient(90deg, transparent, #ef4444, transparent);
        border-radius: 999px;
        animation: frameAccent 2.5s ease infinite;
      }

      .game-selector-label-text {
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #9ca3af;
        white-space: nowrap;
        flex-shrink: 0;
        line-height: 1 !important;
      }

      .priority-filter-btn {
        flex-shrink: 1;
        white-space: nowrap;
        min-width: 0;
        font-size: 0.85rem;
        padding: 6px 12px;
        border-radius: 20px;
        border: 1px solid transparent;
        background: transparent;
        transition: all 0.2s ease;
        cursor: pointer;
        font-weight: 700;
        color: #374151;
        line-height: 1.2 !important;
      }
      .priority-filter-btn.selected {
        background: #ef4444;
        color: white;
        border-color: #ef4444;
        box-shadow: 0 2px 8px rgba(239,68,68,0.3);
      }

      .pulse-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #ef4444;
        flex-shrink: 0;
        animation: selectorPulse 1.8s ease infinite;
      }

      @media (max-width: 640px) {
  .game-selector-frame {
    gap: 6px !important;
    padding: 4px 10px !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    overflow: visible !important;
  }
  .game-selector-label-text {
    font-size: 0.6rem !important;
  }
  .priority-filter-btn {
    font-size: 0.8rem !important;
    padding: 4px 10px !important;
    white-space: normal !important;
  }
  .pulse-dot {
    width: 6px !important;
    height: 6px !important;
  }
}

@media (max-width: 480px) {
  .game-selector-frame {
    gap: 5px !important;
    padding: 6px 10px !important;   /* Aumentado para dar espacio a botones más grandes */
    flex-wrap: wrap !important;
    justify-content: center !important;
    border-radius: 12px !important;
  }
  .game-selector-label-text {
    display: none !important;
  }
  .pulse-dot {
    display: none !important;
  }
  .priority-filter-btn {
    font-size: 1rem !important;      /* Ahora 1rem (antes 0.85) */
    padding: 6px 12px !important;    /* Más espacio interior */
    flex: 1 1 auto !important;
    min-width: 70px !important;      /* Ligeramente más ancho para que el texto no se corte */
    text-align: center !important;
  }
}

@media (max-width: 400px) {
  .game-selector-frame {
    padding: 5px 8px !important;
    gap: 4px !important;
    border-radius: 10px !important;
  }
  .priority-filter-btn {
    font-size: 0.9rem !important;    /* Ahora 0.9rem (antes 0.75) */
    padding: 5px 10px !important;
    min-width: 60px !important;
  }
}
    `;
    document.head.appendChild(s);
  }

  const pulseDot = document.createElement('span');
  pulseDot.className = 'pulse-dot';

  const labelText = document.createElement('span');
  labelText.className = 'game-selector-label-text';
  labelText.textContent = 'Filtrar por TCG';

  const btnsWrap = document.createElement('div');
  btnsWrap.style.cssText = 'display:inline-flex;gap:4px;align-items:center;';

  container.appendChild(pulseDot);
  container.appendChild(labelText);
  container.appendChild(btnsWrap);

  const ALL_DISPLAY_TAGS = [...PRIORITY_TAGS, ...NORMAL_BUTTON_TAGS];
  const liveCounts = {};
  for (const tag of ALL_DISPLAY_TAGS) {
    const needle = tag.toLowerCase();
    let n = 0;
    for (const p of allProducts) {
      const s = p._filterTagsLowerSet || p._tagsLowerSet;
      if (s && s.has(needle)) n++;
    }
    liveCounts[tag] = n;
  }

  const createPriorityBtn = (tag, count) => {
    const btn = document.createElement('button');
    btn.className = [
      'filter-btn tag priority-filter-btn',
      filterState.tagButtons.has(tag) ? 'selected' : '',
    ].join(' ').trim();
    btn.innerHTML =
      `<span class="btn-label">${tag}</span>` +
      `<span class="btn-count">(${count})</span>`;
    btn.addEventListener('click', e => {
      e.preventDefault();
      _showPriorityLoading();
      const isSelected = filterState.tagButtons.has(tag);
      filterState._gameAutoSelected = null;
      if (isSelected) {
        filterState.tagButtons.delete(tag);
      } else {
        PRIORITY_TAGS.forEach(t => filterState.tagButtons.delete(t));
        filterState.tagButtons.add(tag);
      }
      filterState.rebuild();
      if (typeof onChange === 'function') onChange();
    });
    return btn;
  };

  const createBtn = (tag, count) => {
    const btn = document.createElement('button');
    btn.textContent = `${tag} (${count})`;
    btn.className = [
      'filter-btn tag',
      filterState.tagButtons.has(tag) ? 'selected' : '',
    ].join(' ').trim();
    if (!count) {
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    }
    btn.addEventListener('click', e => {
      e.preventDefault();
      if (filterState.tagButtons.has(tag)) filterState.tagButtons.delete(tag);
      else filterState.tagButtons.add(tag);
      filterState.rebuild();
      if (typeof onChange === 'function') onChange();
    });
    return btn;
  };

  PRIORITY_TAGS.forEach(tag =>
    btnsWrap.appendChild(createPriorityBtn(tag, liveCounts[tag] || 0))
  );

  NORMAL_BUTTON_TAGS.forEach(tag =>
    normalEl.appendChild(createBtn(tag, liveCounts[tag] || 0))
  );
}
  /* ═══════════════════════════════════════════════════════════════════════════
     §8  VISIBILITY & VISUAL-CUE HELPERS
  ═══════════════════════════════════════════════════════════════════════════ */

  function updateGroupedFilterVisibility(filterState) {
    const container = document.querySelector('.grouped-filters-container');
    if (!container) return;

    const isOP = [...filterState.selectedTags].some(t =>
      stripDiacritics(t).toLowerCase() === 'one piece tcg');

    const isPK = [...filterState.selectedTags].some(t => {
      const n = stripDiacritics(t).toLowerCase();
      return n === 'pokemon tcg' || n === 'pokémon tcg';
    });

    container.classList.toggle('show-op',      isOP);
    container.classList.toggle('show-pokemon', isPK);
  }

  function updateTagVisualIndicators(filterState, resetBtn) {
    if (resetBtn) {
      resetBtn.classList.toggle('glow', filterState.selectedTags.size > 0);
    }

    const groupMap = [
      [filterState.arquetipos,     'arquetipo-toggle'],
      [filterState.colors,         'color-toggle'],
      [filterState.sets,           'set-toggle'],
      [filterState.costos,         'costo-toggle'],
      [filterState.cartaTipos,     'cartaTipo-toggle'],
      [filterState.poderes,        'poder-toggle'],
      [filterState.rarezas,        'rareza-toggle'],
      [filterState.counters,       'counter-toggle'],
      [filterState.atributos,      'atributo-toggle'],
      [filterState.collections,    'collections-toggle'],
      [filterState.vidas,          'vidas-toggle'],
      [filterState.bloques,        'bloque-toggle'],
      [filterState.starterDecks,   'starterDeck-toggle'],
      [filterState.dons,           'don-toggle'],
      [filterState.pokemonSets,    'pokemonSet-toggle'],
      [filterState.pokemonSupers,  'supertype-toggle'],
      [filterState.pokemonTipos,   'pokemonTipo-toggle'],
      [filterState.pokemonRarezas, 'pokemonRareza-toggle'],
      [filterState.companies,      'company-toggle'],
      [filterState.gradeNos,       'gradeNo-toggle'],
    ];

    groupMap.forEach(([set, id]) => {
      const btn = document.getElementById(id);
      if (btn) btn.classList.toggle('active-group', set.size > 0);
    });
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §9  CONVENIENCE: attach ALL standard group filters at once
  ═══════════════════════════════════════════════════════════════════════════ */

  function attachAllGroupFilters(cfg) {
    const base = (extra) => Object.assign({}, cfg, extra);

    attachGroupFilter(base({ toggleId:'arquetipo-toggle',    dropdownId:'arquetipo-dropdown',    searchId:'arquetipo-search',    optionsId:'arquetipo-options',    tagsSource:arquetipoTags,        selectedSet:cfg.filterState.arquetipos,    optionClass:'arquetipo-option'         }));
    attachGroupFilter(base({ toggleId:'color-toggle',        dropdownId:'color-dropdown',        searchId:'color-search',        optionsId:'color-options',        tagsSource:colorTags,            selectedSet:cfg.filterState.colors,        optionClass:'color-option'             }));
    attachGroupFilter(base({ toggleId:'set-toggle',          dropdownId:'set-dropdown',          searchId:'set-search',          optionsId:'set-options',          tagsSource:setTags,              selectedSet:cfg.filterState.sets,          optionClass:'set-option'               }));
    attachGroupFilter(base({ toggleId:'costo-toggle',        dropdownId:'costo-dropdown',        searchId:'costo-search',        optionsId:'costo-options',        tagsSource:costoTags,            selectedSet:cfg.filterState.costos,        optionClass:'costo-option'             }));
    attachGroupFilter(base({ toggleId:'cartaTipo-toggle',    dropdownId:'cartaTipo-dropdown',    searchId:'cartaTipo-search',    optionsId:'cartaTipo-options',    tagsSource:cartaTipoTags,        selectedSet:cfg.filterState.cartaTipos,    optionClass:'cartaTipo-option'         }));
    attachGroupFilter(base({ toggleId:'poder-toggle',        dropdownId:'poder-dropdown',        searchId:'poder-search',        optionsId:'poder-options',        tagsSource:poderTags,            selectedSet:cfg.filterState.poderes,       optionClass:'poder-option'             }));
    attachGroupFilter(base({ toggleId:'rareza-toggle',       dropdownId:'rareza-dropdown',       searchId:'rareza-search',       optionsId:'rareza-options',       tagsSource:rarezaTags,           selectedSet:cfg.filterState.rarezas,       optionClass:'rareza-option'            }));
    attachGroupFilter(base({ toggleId:'counter-toggle',      dropdownId:'counter-dropdown',      searchId:'counter-search',      optionsId:'counter-options',      tagsSource:counterTags,          selectedSet:cfg.filterState.counters,      optionClass:'group-option'             }));
    attachGroupFilter(base({ toggleId:'atributo-toggle',     dropdownId:'atributo-dropdown',     searchId:'atributo-search',     optionsId:'atributo-options',     tagsSource:atributoTags,         selectedSet:cfg.filterState.atributos,     optionClass:'group-option'             }));
    attachGroupFilter(base({ toggleId:'collections-toggle',  dropdownId:'collections-dropdown',  searchId:'collections-search',  optionsId:'collections-options',  tagsSource:collectionsTags,      selectedSet:cfg.filterState.collections,   optionClass:'collections-option'       }));
    attachGroupFilter(base({ toggleId:'vidas-toggle',        dropdownId:'vidas-dropdown',        searchId:'vidas-search',        optionsId:'vidas-options',        tagsSource:vidasTags,            selectedSet:cfg.filterState.vidas,         optionClass:'vidas-option'             }));
    attachGroupFilter(base({ toggleId:'bloque-toggle',       dropdownId:'bloque-dropdown',       searchId:'bloque-search',       optionsId:'bloque-options',       tagsSource:bloqueTags,           selectedSet:cfg.filterState.bloques,       optionClass:'bloque-option'            }));
    attachGroupFilter(base({ toggleId:'starterDeck-toggle',  dropdownId:'starterDeck-dropdown',  searchId:'starterDeck-search',  optionsId:'starterDeck-options',  tagsSource:starterDeckTags,      selectedSet:cfg.filterState.starterDecks,  optionClass:'starterDeck-option'       }));
    attachGroupFilter(base({ toggleId:'don-toggle',          dropdownId:'don-dropdown',          searchId:'don-search',          optionsId:'don-options',          tagsSource:donTags,              selectedSet:cfg.filterState.dons,          optionClass:'group-option'             }));
    attachGroupFilter(base({ toggleId:'company-toggle',      dropdownId:'company-dropdown',      searchId:'company-search',      optionsId:'company-options',      tagsSource:companyTags,          selectedSet:cfg.filterState.companies,     optionClass:'group-option'             }));
    attachGroupFilter(base({ toggleId:'gradeNo-toggle',      dropdownId:'gradeNo-dropdown',      searchId:'gradeNo-search',      optionsId:'gradeNo-options',      tagsSource:gradeNoTags,          selectedSet:cfg.filterState.gradeNos,      optionClass:'group-option'             }));
    attachGroupFilter(base({ toggleId:'pokemonSet-toggle',   dropdownId:'pokemonSet-dropdown',   searchId:'pokemonSet-search',   optionsId:'pokemonSet-options',   tagsSource:pokemonSetTags,       selectedSet:cfg.filterState.pokemonSets,   optionClass:'pokemon-set-option'       }));
    attachGroupFilter(base({ toggleId:'supertype-toggle',    dropdownId:'supertype-dropdown',    searchId:'supertype-search',    optionsId:'supertype-options',    tagsSource:pokemonSupertypeTags, selectedSet:cfg.filterState.pokemonSupers, optionClass:'pokemon-supertype-option' }));
    attachGroupFilter(base({ toggleId:'pokemonTipo-toggle',  dropdownId:'pokemonTipo-dropdown',  searchId:'pokemonTipo-search',  optionsId:'pokemonTipo-options',  tagsSource:pokemonTipoTags,      selectedSet:cfg.filterState.pokemonTipos,  optionClass:'pokemon-tipo-option'      }));
    attachGroupFilter(base({ toggleId:'pokemonRareza-toggle',dropdownId:'pokemonRareza-dropdown',searchId:'pokemonRareza-search',optionsId:'pokemonRareza-options',tagsSource:pokemonRarezaTags,   selectedSet:cfg.filterState.pokemonRarezas,optionClass:'pokemon-rareza-option'    }));
  }


  /* ═══════════════════════════════════════════════════════════════════════════
     §10  PUBLIC API
  ═══════════════════════════════════════════════════════════════════════════ */

  global.TCGFilters = {
    /* ── Dictionaries ── */
    arquetipoTags,
    colorTags,
    setTags,
    costoTags,
    cartaTipoTags,
    poderTags,
    rarezaTags,
    counterTags,
    atributoTags,
    collectionsTags,
    vidasTags,
    bloqueTags,
    starterDeckTags,
    donTags,
    companyTags,
    gradeNoTags,
    pokemonRarezaTags,
    pokemonTipoTags,
    pokemonSupertypeTags,
    pokemonSetTags,

    /* ── Derived sets ── */
    POKEMON_WHITELIST_TAGS,
    ALL_GROUPED_TAGS,
    ALL_KNOWN_BUTTON_TAGS,   // ← curated whitelist for pill buttons
    GAME_SIGNATURES,
    GAME_TAG,

    /* ── Utilities ── */
    normalizeTag,
    normLow,
    stripDiacritics,
    shouldHideTagFromUI,

    /* ── Performance helpers ── */
    indexProductTags,
    buildTagCounts,
    filterProducts,

    /* ── State ── */
    createFilterState,
    resetAllFilters,

    /* ── UI builders ── */
    attachGroupFilter,
    attachAllGroupFilters,
    renderTagButtons,

    /* ── Visual helpers ── */
    updateGroupedFilterVisibility,
    updateTagVisualIndicators,

    /* ── Auto-detection ── */     // ← NEW
    autoDetectAndSelectGame,
  };

})(window);
